import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import QRCodeStyling from "qr-code-styling";
import {
  Download,
  QrCode,
  Search,
  Check,
  Palette,
  Shapes,
  Image as ImageIcon,
  SlidersHorizontal,
  RotateCcw,
  Upload,
  X,
  Lock,
  Save,
  Frame as FrameIcon,
} from "lucide-react";
import { useAppServices } from "../../../hook/services";
import { useMarketplace } from "../../../marketplace/context";
import FeatureGate, {
  useFeature,
  UpgradeBadge,
} from "../../../marketplace/FeatureGate";
import { Link as LinkType, QrTemplate } from "../../../types";
import HeroSection from "../../../components/ui/HeroSection";
import EmptyState from "../../../components/ui/EmptyState";
import Button from "../../../components/ui/Button";
import Badge from "../../../components/ui/Badge";
import Skeleton from "../../../components/ui/Skeleton";
import HelpHint from "../../../components/ui/HelpHint";
import { buildRedirectUrl } from "../../../utils";
import {
  DEFAULT_CONFIG,
  PRESETS,
  QRConfig,
  ColorOption,
  DotType,
  CornerSquareType,
  CornerDotType,
  ErrorLevel,
  FrameStyle,
} from "./config";

type Tab = "colors" | "shapes" | "logo" | "frame" | "advanced";

// Corner radius (px) used for a frame style in both preview + export.
const frameRadius = (style: FrameStyle) =>
  style === "rounded" ? 28 : style === "box" ? 6 : 16;

// --- helpers --------------------------------------------------------------

const toQrColorOpt = (c: ColorOption) => {
  if (c.mode === "gradient") {
    return {
      gradient: {
        type: c.gradient.type,
        rotation: c.gradient.rotation,
        colorStops: [
          { offset: 0, color: c.gradient.colors[0] },
          { offset: 1, color: c.gradient.colors[1] },
        ],
      },
    } as any;
  }
  return { color: c.color } as any;
};

const buildQrOptions = (cfg: QRConfig, data: string) => ({
  width: cfg.size,
  height: cfg.size,
  type: "canvas" as const,
  data: data || " ",
  margin: cfg.margin,
  qrOptions: { errorCorrectionLevel: cfg.errorCorrection },
  dotsOptions: { type: cfg.dot.style, ...toQrColorOpt(cfg.dot) },
  cornersSquareOptions: {
    type: cfg.cornerSquare.style,
    ...toQrColorOpt(cfg.cornerSquare),
  },
  cornersDotOptions: {
    type: cfg.cornerDot.style,
    ...toQrColorOpt(cfg.cornerDot),
  },
  backgroundOptions: toQrColorOpt(cfg.background),
  image: cfg.logo.src ?? undefined,
  imageOptions: {
    hideBackgroundDots: cfg.logo.hideBackgroundDots,
    imageSize: cfg.logo.size,
    margin: cfg.logo.margin,
    crossOrigin: "anonymous" as const,
  },
});

const blobToImage = (blob: Blob): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = reject;
    img.src = url;
  });

const roundRectPath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) => {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
};

// Composite the rendered QR onto a framed canvas with the CTA text below.
const renderFramedCanvas = async (
  cfg: QRConfig,
  qr: QRCodeStyling,
): Promise<HTMLCanvasElement | null> => {
  const blob = (await qr.getRawData("png")) as Blob | null;
  if (!blob) return null;
  const img = await blobToImage(blob);
  const f = cfg.frame;
  const qrSize = cfg.size;
  const pad = Math.round(qrSize * 0.08);
  const hasText = !!f.text.trim();
  const textH = hasText ? Math.round(qrSize * 0.17) : 0;
  const radius = frameRadius(f.style);
  const W = qrSize + pad * 2;
  const H = qrSize + pad * 2 + textH;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const label = f.text.trim().toUpperCase();

  if (f.style === "banner") {
    roundRectPath(ctx, 0, 0, W, H, 18);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.drawImage(img, pad, pad, qrSize, qrSize);
    if (hasText) {
      const barH = Math.round(textH * 0.72);
      const barY = qrSize + pad + Math.round((textH - barH) / 2);
      roundRectPath(ctx, pad, barY, W - pad * 2, barH, barH / 2);
      ctx.fillStyle = f.color;
      ctx.fill();
      ctx.fillStyle = f.textColor;
      ctx.font = `700 ${Math.round(barH * 0.42)}px Inter, system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(label, W / 2, barY + barH / 2);
    }
  } else {
    roundRectPath(ctx, 0, 0, W, H, radius);
    ctx.fillStyle = f.color;
    ctx.fill();
    roundRectPath(ctx, pad * 0.5, pad * 0.5, W - pad, qrSize + pad, Math.max(0, radius - 6));
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.drawImage(img, pad, pad, qrSize, qrSize);
    if (hasText) {
      ctx.fillStyle = f.textColor;
      ctx.font = `700 ${Math.round(textH * 0.42)}px Inter, system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(label, W / 2, qrSize + pad * 2 + textH / 2);
    }
  }
  return canvas;
};

// --- small UI primitives -------------------------------------------------

const Section = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-3">
    <div>
      <h4 className="text-[12px] font-semibold uppercase tracking-wider text-ink-500">
        {title}
      </h4>
      {description && (
        <p className="text-xs text-ink-400 mt-0.5">{description}</p>
      )}
    </div>
    {children}
  </div>
);

const Field = ({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) => (
  <label className="block">
    <div className="flex items-baseline justify-between mb-1.5">
      <span className="text-xs font-medium text-ink-700">{label}</span>
      {hint && <span className="text-[10px] text-ink-400">{hint}</span>}
    </div>
    {children}
  </label>
);

const ColorSwatchInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => (
  <div className="relative inline-flex items-center gap-2 bg-white border border-ink-200 rounded-lg pl-1 pr-2 h-9 hover:border-ink-300 transition-colors">
    <span
      className="w-7 h-7 rounded-md border border-ink-200/70 shrink-0 cursor-pointer overflow-hidden relative"
      style={{ background: value }}
    >
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 opacity-0 cursor-pointer"
      />
    </span>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-20 text-xs font-mono bg-transparent text-ink-700 focus:outline-none uppercase"
    />
  </div>
);

const Chip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`h-8 px-3 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
      active
        ? "bg-ink-900 text-white shadow-sm"
        : "bg-white border border-ink-200 text-ink-600 hover:text-ink-900 hover:border-ink-300"
    }`}
  >
    {children}
  </button>
);

const StyleTile = ({
  active,
  onClick,
  label,
  preview,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  preview: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`group flex flex-col items-center gap-2 p-2 rounded-xl border transition-all ${
      active
        ? "border-brand-500 bg-brand-50/40 shadow-sm"
        : "border-ink-200 hover:border-ink-300 bg-white"
    }`}
  >
    <div className="w-14 h-14 flex items-center justify-center text-ink-700 group-hover:text-ink-900">
      {preview}
    </div>
    <span className="text-[11px] text-ink-600 font-medium">{label}</span>
  </button>
);

// SVG mini-previews for dot styles
const DotStylePreviews: Record<DotType, React.ReactNode> = {
  square: (
    <svg width="44" height="44" viewBox="0 0 44 44">
      {[0, 12, 24, 36].map((y) =>
        [0, 12, 24, 36].map((x) => (
          <rect
            key={`${x}-${y}`}
            x={x}
            y={y}
            width="8"
            height="8"
            fill="currentColor"
          />
        )),
      )}
    </svg>
  ),
  dots: (
    <svg width="44" height="44" viewBox="0 0 44 44">
      {[0, 12, 24, 36].map((y) =>
        [0, 12, 24, 36].map((x) => (
          <circle
            key={`${x}-${y}`}
            cx={x + 4}
            cy={y + 4}
            r="3.5"
            fill="currentColor"
          />
        )),
      )}
    </svg>
  ),
  rounded: (
    <svg width="44" height="44" viewBox="0 0 44 44">
      {[0, 12, 24, 36].map((y) =>
        [0, 12, 24, 36].map((x) => (
          <rect
            key={`${x}-${y}`}
            x={x}
            y={y}
            width="8"
            height="8"
            rx="2"
            fill="currentColor"
          />
        )),
      )}
    </svg>
  ),
  "extra-rounded": (
    <svg width="44" height="44" viewBox="0 0 44 44">
      {[0, 12, 24, 36].map((y) =>
        [0, 12, 24, 36].map((x) => (
          <rect
            key={`${x}-${y}`}
            x={x}
            y={y}
            width="8"
            height="8"
            rx="3.5"
            fill="currentColor"
          />
        )),
      )}
    </svg>
  ),
  classy: (
    <svg width="44" height="44" viewBox="0 0 44 44">
      {[0, 12, 24, 36].map((y) =>
        [0, 12, 24, 36].map((x) => (
          <path
            key={`${x}-${y}`}
            d={`M${x + 1} ${y} h6 a1 1 0 0 1 1 1 v6 a1 1 0 0 1 -1 1 h-6 a1 1 0 0 1 -1 -1 v-6 a1 1 0 0 1 1 -1 Z`}
            fill="currentColor"
          />
        )),
      )}
    </svg>
  ),
  "classy-rounded": (
    <svg width="44" height="44" viewBox="0 0 44 44">
      {[0, 12, 24, 36].map((y) =>
        [0, 12, 24, 36].map((x) => (
          <rect
            key={`${x}-${y}`}
            x={x}
            y={y}
            width="8"
            height="8"
            rx="2.5"
            fill="currentColor"
          />
        )),
      )}
    </svg>
  ),
};

const CornerSquarePreviews: Record<CornerSquareType, React.ReactNode> = {
  square: (
    <svg width="40" height="40" viewBox="0 0 40 40">
      <rect
        x="3"
        y="3"
        width="34"
        height="34"
        stroke="currentColor"
        strokeWidth="6"
        fill="none"
      />
    </svg>
  ),
  dot: (
    <svg width="40" height="40" viewBox="0 0 40 40">
      <circle
        cx="20"
        cy="20"
        r="17"
        stroke="currentColor"
        strokeWidth="6"
        fill="none"
      />
    </svg>
  ),
  "extra-rounded": (
    <svg width="40" height="40" viewBox="0 0 40 40">
      <rect
        x="3"
        y="3"
        width="34"
        height="34"
        rx="10"
        stroke="currentColor"
        strokeWidth="6"
        fill="none"
      />
    </svg>
  ),
};

const CornerDotPreviews: Record<CornerDotType, React.ReactNode> = {
  square: (
    <svg width="36" height="36" viewBox="0 0 36 36">
      <rect x="8" y="8" width="20" height="20" fill="currentColor" />
    </svg>
  ),
  dot: (
    <svg width="36" height="36" viewBox="0 0 36 36">
      <circle cx="18" cy="18" r="10" fill="currentColor" />
    </svg>
  ),
};

// --- Color block (solid / gradient) --------------------------------------

const ColorBlock = ({
  label,
  value,
  onChange,
  description,
}: {
  label: string;
  value: ColorOption;
  onChange: (v: ColorOption) => void;
  description?: string;
}) => {
  const setMode = (mode: "solid" | "gradient") => {
    onChange({ ...value, mode });
  };
  const setSolid = (c: string) => {
    onChange({ ...value, color: c, gradient: { ...value.gradient, colors: [c, value.gradient.colors[1]] } });
  };
  const setGradColor = (idx: 0 | 1, c: string) => {
    const colors: [string, string] = [...value.gradient.colors] as any;
    colors[idx] = c;
    onChange({ ...value, gradient: { ...value.gradient, colors } });
  };
  const setGradType = (t: "linear" | "radial") => {
    onChange({ ...value, gradient: { ...value.gradient, type: t } });
  };
  const setGradRotation = (n: number) => {
    onChange({ ...value, gradient: { ...value.gradient, rotation: n } });
  };

  return (
    <div className="border border-ink-200/70 rounded-xl p-3 bg-white/70">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[12px] font-semibold text-ink-800">{label}</div>
          {description && (
            <div className="text-[11px] text-ink-400 mt-0.5">{description}</div>
          )}
        </div>
        <div className="inline-flex p-0.5 bg-ink-100 rounded-md">
          <button
            type="button"
            onClick={() => setMode("solid")}
            className={`h-6 px-2 rounded text-[11px] font-medium ${value.mode === "solid" ? "bg-white text-ink-900 shadow-sm" : "text-ink-500"}`}
          >
            Solid
          </button>
          <button
            type="button"
            onClick={() => setMode("gradient")}
            className={`h-6 px-2 rounded text-[11px] font-medium ${value.mode === "gradient" ? "bg-white text-ink-900 shadow-sm" : "text-ink-500"}`}
          >
            Gradient
          </button>
        </div>
      </div>

      {value.mode === "solid" ? (
        <ColorSwatchInput value={value.color} onChange={setSolid} />
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <ColorSwatchInput
              value={value.gradient.colors[0]}
              onChange={(c) => setGradColor(0, c)}
            />
            <span className="text-ink-400 text-xs">→</span>
            <ColorSwatchInput
              value={value.gradient.colors[1]}
              onChange={(c) => setGradColor(1, c)}
            />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="inline-flex p-0.5 bg-ink-100 rounded-md">
              <button
                type="button"
                onClick={() => setGradType("linear")}
                className={`h-6 px-2 rounded text-[11px] font-medium ${value.gradient.type === "linear" ? "bg-white text-ink-900 shadow-sm" : "text-ink-500"}`}
              >
                Linear
              </button>
              <button
                type="button"
                onClick={() => setGradType("radial")}
                className={`h-6 px-2 rounded text-[11px] font-medium ${value.gradient.type === "radial" ? "bg-white text-ink-900 shadow-sm" : "text-ink-500"}`}
              >
                Radial
              </button>
            </div>
            {value.gradient.type === "linear" && (
              <div className="flex items-center gap-2 flex-1 min-w-[160px]">
                <span className="text-[10px] text-ink-500 shrink-0">Angle</span>
                <input
                  type="range"
                  min={0}
                  max={Math.PI * 2}
                  step={0.05}
                  value={value.gradient.rotation}
                  onChange={(e) => setGradRotation(parseFloat(e.target.value))}
                  className="flex-1 accent-brand-500"
                />
                <span className="text-[10px] text-ink-500 tabular-nums w-8">
                  {Math.round((value.gradient.rotation * 180) / Math.PI)}°
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// --- Main component -------------------------------------------------------

const QRStudio = () => {
  const services = useAppServices();
  const { buildPath } = useMarketplace();
  const colorsFeat = useFeature("qrColors");
  const shapesFeat = useFeature("qrShapes");
  const logoFeat = useFeature("qrLogo");
  const advancedFeat = useFeature("qrAdvanced");
  const [searchParams] = useSearchParams();
  const [links, setLinks] = useState<LinkType[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<Tab>("colors");
  const [config, setConfig] = useState<QRConfig>(DEFAULT_CONFIG);
  const [templates, setTemplates] = useState<QrTemplate[]>([]);
  const [tmplName, setTmplName] = useState("");
  const [savingTmpl, setSavingTmpl] = useState(false);
  const [batchIds, setBatchIds] = useState<Set<string>>(new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const [savingLink, setSavingLink] = useState(false);
  const [savedLink, setSavedLink] = useState(false);
  const loadedConfigRef = useRef<string | null>(null);

  const qrRef = useRef<QRCodeStyling | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      const { response } = await services.url_tracker.get();
      if (Array.isArray(response)) {
        setLinks(response);
        const presel = searchParams.get("link");
        if (presel && response.find((l: LinkType) => l._id === presel)) {
          setSelectedId(presel);
        } else if (response.length > 0) {
          setSelectedId(response[0]._id);
        }
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = links.find((l) => l._id === selectedId) || null;
  const redirectUrl = selected
    ? buildRedirectUrl(selected.slug || selected._id, selected.domain_host)
    : "";

  // Load the selected link's saved QR style — once per link, so it survives a
  // reload and switching links pulls up each one's own design.
  useEffect(() => {
    if (!selected || loadedConfigRef.current === selected._id) return;
    loadedConfigRef.current = selected._id;
    // Load this link's saved style, or reset to default — so a link with no
    // saved config never inherits the previously-selected link's edits.
    setConfig(
      selected.qr_config && typeof selected.qr_config === "object"
        ? { ...DEFAULT_CONFIG, ...(selected.qr_config as Partial<QRConfig>) }
        : DEFAULT_CONFIG,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, links]);

  // Always hold the latest config/data so the callback ref below can build the
  // QR with current values regardless of render timing.
  const latest = useRef({ config, redirectUrl });
  latest.current = { config, redirectUrl };

  // Callback ref: create + attach the QR the instant the preview container
  // mounts. This is reliable even though the container is gated behind the
  // `loading` state (an effect keyed on a ref wouldn't re-run when it attaches).
  const attachContainer = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    if (!node) return;
    if (!qrRef.current) {
      qrRef.current = new QRCodeStyling(
        buildQrOptions(latest.current.config, latest.current.redirectUrl),
      );
    }
    qrRef.current.append(node);
  }, []);

  // Keep the QR in sync with config / selected link.
  useEffect(() => {
    if (!qrRef.current) return;
    qrRef.current.update(buildQrOptions(config, redirectUrl));
  }, [config, redirectUrl]);

  // Persist the current style onto the selected link so it reloads next time.
  const saveToLink = async () => {
    if (!selected) return;
    setSavingLink(true);
    setSavedLink(false);
    const { error } = await services.url_tracker.update({
      payload: { _id: selected._id, qr_config: config },
    });
    setSavingLink(false);
    if (!error) {
      setLinks((curr) =>
        curr.map((l) =>
          l._id === selected._id ? { ...l, qr_config: config } : l,
        ),
      );
      setSavedLink(true);
      setTimeout(() => setSavedLink(false), 1800);
    }
  };

  const download = async (extension: "png" | "svg" | "jpeg") => {
    const name = `qr-${(selected?.title || "link").toLowerCase().replace(/\s+/g, "-")}`;
    // SVG can't be composited here — export the plain QR. PNG/JPEG get the frame.
    if (config.frame.enabled && extension !== "svg" && qrRef.current) {
      const canvas = await renderFramedCanvas(config, qrRef.current);
      if (canvas) {
        const a = document.createElement("a");
        a.href = canvas.toDataURL(
          extension === "jpeg" ? "image/jpeg" : "image/png",
          0.95,
        );
        a.download = `${name}.${extension}`;
        a.click();
        return;
      }
    }
    qrRef.current?.download({ name, extension });
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setConfig((c) => ({
        ...c,
        logo: { ...c.logo, src: reader.result as string },
      }));
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setConfig((c) => ({ ...c, logo: { ...c.logo, src: null } }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const applyPreset = (presetIdx: number) => {
    const p = PRESETS[presetIdx];
    setConfig((c) => ({ ...c, ...p.config }));
  };

  const reset = () => setConfig(DEFAULT_CONFIG);

  // --- saved templates ---
  const loadTemplates = async () => {
    const { response } = await services.qr_templates.get();
    if (Array.isArray(response)) setTemplates(response);
  };
  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveTemplate = async () => {
    const name = tmplName.trim();
    if (!name) return;
    setSavingTmpl(true);
    const { error } = await services.qr_templates.create({
      payload: { name, config },
    });
    setSavingTmpl(false);
    if (!error) {
      setTmplName("");
      loadTemplates();
    }
  };

  const applyTemplate = (t: QrTemplate) =>
    setConfig({ ...DEFAULT_CONFIG, ...(t.config as Partial<QRConfig>) });

  const deleteTemplate = async (id: string) => {
    await services.qr_templates.delete({ query: `_id=${id}` });
    loadTemplates();
  };

  // --- batch export ---
  const toggleBatch = (id: string) =>
    setBatchIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Render one link's QR (framed if enabled) to a PNG blob.
  const qrBlobFor = async (link: LinkType): Promise<Blob | null> => {
    const url = buildRedirectUrl(link.slug || link._id, link.domain_host);
    const qr = new QRCodeStyling(buildQrOptions(config, url));
    if (config.frame.enabled) {
      const canvas = await renderFramedCanvas(config, qr);
      if (!canvas) return null;
      return new Promise((res) => canvas.toBlob((b) => res(b), "image/png"));
    }
    return (await qr.getRawData("png")) as Blob | null;
  };

  const downloadBatch = async () => {
    if (!batchIds.size) return;
    setBatchBusy(true);
    try {
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();
      const used = new Set<string>();
      for (const id of Array.from(batchIds)) {
        const link = links.find((l) => l._id === id);
        if (!link) continue;
        const blob = await qrBlobFor(link);
        if (!blob) continue;
        const base = `qr-${(link.title || link._id)
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^a-z0-9-]/g, "")}`;
        let name = `${base}.png`;
        let i = 2;
        while (used.has(name)) name = `${base}-${i++}.png`;
        used.add(name);
        zip.file(name, blob);
      }
      const out = await zip.generateAsync({ type: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(out);
      a.download = "qr-codes.zip";
      a.click();
      URL.revokeObjectURL(a.href);
    } finally {
      setBatchBusy(false);
    }
  };

  const filteredLinks = useMemo(
    () =>
      links.filter(
        (l) =>
          !search ||
          l.title?.toLowerCase().includes(search.toLowerCase()) ||
          l.url?.toLowerCase().includes(search.toLowerCase()),
      ),
    [links, search],
  );

  // --- Renders --------------------------------------------------------

  return (
    <div className="space-y-6">
      <HeroSection
        eyebrow={
          <>
            <QrCode size={11} className="text-brand-500" />
            <span>QR Studio</span>
          </>
        }
        title={
          <>
            Print-ready{" "}
            <span className="bg-brand-gradient bg-clip-text text-transparent">
              QR codes
            </span>
            .
          </>
        }
        description="Customize colors, shapes and add your logo. Every scan tracks just like a click."
      />

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Skeleton className="h-[480px] rounded-2xl" />
          <Skeleton className="h-[480px] rounded-2xl lg:col-span-2" />
        </div>
      ) : links.length === 0 ? (
        <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card">
          <EmptyState
            icon={<QrCode size={26} />}
            tone="violet"
            title="No links to make QR codes for"
            description="Create your first short link, then come back to design scannable QR codes."
            action={
              <RouterLink to={buildPath("links?new=1")}>
                <Button variant="gradient">Create a link</Button>
              </RouterLink>
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* LEFT: link picker */}
          <div className="lg:col-span-1 bg-white border border-ink-200/70 rounded-2xl shadow-card overflow-hidden flex flex-col max-h-[800px]">
            <div className="px-5 pt-5 pb-3 border-b border-ink-100">
              <h3 className="text-sm font-semibold text-ink-900 tracking-tight">
                Select a link
              </h3>
              <div className="relative mt-3">
                <Search
                  size={13}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-8 pr-3 h-8 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {filteredLinks.map((link) => {
                const isActive = selectedId === link._id;
                const inBatch = batchIds.has(link._id);
                return (
                  <div
                    key={link._id}
                    className={`group w-full flex items-stretch gap-2 transition-colors ${
                      isActive ? "bg-brand-50/50" : "hover:bg-ink-50/60"
                    }`}
                  >
                    <div
                      className={`w-1 self-stretch rounded-r-full transition-colors ${
                        isActive
                          ? "bg-brand-500"
                          : "bg-transparent group-hover:bg-ink-200"
                      }`}
                    />
                    <label
                      className="flex items-center pl-1.5 cursor-pointer"
                      title="Add to batch"
                    >
                      <input
                        type="checkbox"
                        checked={inBatch}
                        onChange={() => toggleBatch(link._id)}
                        className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-2 focus:ring-brand-500/30"
                      />
                    </label>
                    <button
                      onClick={() => setSelectedId(link._id)}
                      className="min-w-0 flex-1 text-left py-2.5 pr-3 flex items-center gap-2"
                    >
                      <div className="min-w-0 flex-1">
                        <div
                          className={`text-sm font-medium truncate ${
                            isActive ? "text-brand-900" : "text-ink-900"
                          }`}
                        >
                          {link.title || "Untitled"}
                        </div>
                        <div className="text-xs text-ink-500 truncate mt-0.5">
                          {link.url}
                        </div>
                      </div>
                      {isActive && (
                        <Check
                          size={14}
                          className="text-brand-600 shrink-0 self-center"
                        />
                      )}
                    </button>
                  </div>
                );
              })}
              {filteredLinks.length === 0 && (
                <div className="text-sm text-ink-400 text-center py-6">
                  No matches.
                </div>
              )}
            </div>
            {/* Batch export footer */}
            <div className="border-t border-ink-100 p-3 space-y-2">
              <div className="flex items-center justify-between text-[11px] text-ink-500">
                <span className="flex items-center">
                  {batchIds.size} selected for batch
                  <HelpHint title="Batch export" anchor="qr-batch">
                    Tick multiple links and download a QR image for each at once
                    (as a ZIP) with your current style applied to all of them.
                  </HelpHint>
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      setBatchIds(new Set(filteredLinks.map((l) => l._id)))
                    }
                    className="hover:text-ink-800 font-medium"
                  >
                    Select all
                  </button>
                  {batchIds.size > 0 && (
                    <button
                      onClick={() => setBatchIds(new Set())}
                      className="hover:text-ink-800 font-medium"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <Button
                variant="primary"
                size="sm"
                className="w-full"
                onClick={downloadBatch}
                disabled={!batchIds.size || batchBusy}
                icon={<Download size={14} />}
              >
                {batchBusy
                  ? "Preparing…"
                  : `Download ${batchIds.size || ""} as ZIP`}
              </Button>
            </div>
          </div>

          {/* RIGHT: preview + customize */}
          <div className="lg:col-span-2 space-y-4">
            {/* Preview card */}
            <div className="relative bg-white border border-ink-200/70 rounded-2xl shadow-card overflow-hidden">
              <div className="absolute inset-0 bg-mesh-1 opacity-40" />
              <div className="relative px-6 py-7 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-brand-200/40 via-violet-200/30 to-transparent blur-2xl" />
                    {/* Keep the QR container mounted regardless of frame so the
                        rendered canvas isn't lost when toggling the frame. */}
                    <div
                      className="relative shadow-card-elevated"
                      style={
                        config.frame.enabled
                          ? {
                              background:
                                config.frame.style === "banner"
                                  ? "#ffffff"
                                  : config.frame.color,
                              borderRadius: frameRadius(config.frame.style),
                              padding: 14,
                            }
                          : { borderRadius: 16 }
                      }
                    >
                      <div
                        ref={attachContainer}
                        className={
                          config.frame.enabled
                            ? "rounded-xl bg-white overflow-hidden"
                            : "p-5 rounded-2xl border border-ink-200/70 bg-white"
                        }
                      />
                      {config.frame.enabled &&
                        config.frame.text.trim() &&
                        (config.frame.style === "banner" ? (
                          <div
                            className="mx-auto mt-3 px-4 py-1.5 rounded-full text-center text-sm font-bold tracking-wide w-fit"
                            style={{
                              background: config.frame.color,
                              color: config.frame.textColor,
                            }}
                          >
                            {config.frame.text.toUpperCase()}
                          </div>
                        ) : (
                          <div
                            className="text-center font-bold tracking-wide pt-2.5 text-sm"
                            style={{ color: config.frame.textColor }}
                          >
                            {config.frame.text.toUpperCase()}
                          </div>
                        ))}
                    </div>
                  </div>
                  <code className="text-[11px] text-ink-500 mt-4 text-center break-all max-w-xs font-mono">
                    {redirectUrl}
                  </code>
                </div>

                <div className="space-y-4">
                  <div>
                    <Badge tone="brand">Selected link</Badge>
                    <h3 className="font-display font-semibold text-ink-900 text-xl truncate mt-2">
                      {selected?.title || "Untitled"}
                    </h3>
                    <p className="text-xs text-ink-500 mt-0.5 truncate">
                      {selected?.url}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                      This link's style
                    </div>
                    <Button
                      variant="primary"
                      onClick={saveToLink}
                      loading={savingLink}
                      disabled={!selected}
                      icon={
                        savedLink ? (
                          <Check size={14} className="text-emerald-300" />
                        ) : (
                          <Save size={14} />
                        )
                      }
                    >
                      {savedLink ? "Saved" : "Save style to this link"}
                    </Button>
                    <p className="text-[11px] text-ink-400">
                      Saves this customization to the link so it's still here when
                      you come back.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                      Download
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="gradient"
                        onClick={() => download("png")}
                        icon={<Download size={14} strokeWidth={2.25} />}
                      >
                        PNG
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => download("svg")}
                        icon={<Download size={14} strokeWidth={2.25} />}
                      >
                        SVG
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => download("jpeg")}
                        icon={<Download size={14} strokeWidth={2.25} />}
                      >
                        JPEG
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                      Presets
                    </div>
                    <div className="grid grid-cols-4 gap-1.5">
                      {PRESETS.map((p, idx) => (
                        <button
                          key={p.name}
                          onClick={() => applyPreset(idx)}
                          title={p.description}
                          className="group flex items-center gap-1.5 px-2 h-8 rounded-lg border border-ink-200 bg-white hover:border-brand-400 hover:shadow-sm transition-all"
                        >
                          <span
                            className="w-3 h-3 rounded-sm shrink-0 border border-ink-200/60"
                            style={{ background: p.thumbColor }}
                          />
                          <span className="text-[11px] font-medium text-ink-700 group-hover:text-ink-900 truncate">
                            {p.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Saved templates — reuse a branded look across links. */}
                  <div className="space-y-2">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 flex items-center">
                      My templates
                      <HelpHint title="QR templates" anchor="qr-templates">
                        Save the current style as a named template and apply it
                        to any link in one click, so all your QR codes stay
                        on-brand.
                      </HelpHint>
                    </div>
                    {templates.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {templates.map((t) => (
                          <div
                            key={t._id}
                            className="group inline-flex items-center gap-1 pl-2.5 pr-1 h-8 rounded-lg border border-ink-200 bg-white hover:border-brand-400 hover:shadow-sm transition-all"
                          >
                            <button
                              onClick={() => applyTemplate(t)}
                              title="Apply template"
                              className="text-[11px] font-medium text-ink-700 group-hover:text-ink-900 truncate max-w-[120px]"
                            >
                              {t.name}
                            </button>
                            <button
                              onClick={() => deleteTemplate(t._id)}
                              title="Delete template"
                              className="w-5 h-5 rounded text-ink-300 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center shrink-0"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-1.5">
                      <input
                        type="text"
                        value={tmplName}
                        onChange={(e) => setTmplName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            saveTemplate();
                          }
                        }}
                        placeholder="Save current style as…"
                        maxLength={60}
                        className="flex-1 h-8 px-2.5 text-[11px] bg-white border border-ink-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400"
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={saveTemplate}
                        disabled={!tmplName.trim() || savingTmpl}
                        icon={<Save size={12} />}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Customize card */}
            <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card overflow-hidden">
              {/* Tabs — always visible. Locked tabs show a Pro badge but stay
                  clickable so users can peek at what an upgrade unlocks. */}
              <div className="flex items-center justify-between px-4 pt-4 gap-3 flex-wrap">
                <div className="inline-flex p-0.5 bg-ink-100 rounded-lg">
                  {(
                    [
                      { key: "colors", label: "Colors", icon: Palette, feat: colorsFeat, feature: "qrColors" as const },
                      { key: "shapes", label: "Shapes", icon: Shapes, feat: shapesFeat, feature: "qrShapes" as const },
                      { key: "logo", label: "Logo", icon: ImageIcon, feat: logoFeat, feature: "qrLogo" as const },
                      { key: "frame", label: "Frame", icon: FrameIcon, feat: advancedFeat, feature: "qrAdvanced" as const },
                      { key: "advanced", label: "Advanced", icon: SlidersHorizontal, feat: advancedFeat, feature: "qrAdvanced" as const },
                    ] as { key: Tab; label: string; icon: any; feat: { allowed: boolean; minTierLabel: string }; feature: any }[]
                  ).map(({ key, label, icon: Icon, feat, feature }) => (
                    <button
                      key={key}
                      onClick={() => setTab(key)}
                      title={!feat.allowed ? `Available in ${feat.minTierLabel}` : undefined}
                      className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium transition-all ${
                        tab === key
                          ? "bg-white text-ink-900 shadow-sm"
                          : "text-ink-500 hover:text-ink-900"
                      }`}
                    >
                      {!feat.allowed ? (
                        <Lock size={11} className="text-ink-400" />
                      ) : (
                        <Icon size={13} />
                      )}
                      {label}
                      {!feat.allowed && <UpgradeBadge feature={feature} compact />}
                    </button>
                  ))}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={reset}
                  icon={<RotateCcw size={12} />}
                >
                  Reset
                </Button>
              </div>

              <div className="p-5 space-y-6">
                {tab === "colors" && (
                  <FeatureGate feature="qrColors" disabledOverlay>
                  <>
                    <ColorBlock
                      label="Dots"
                      description="The little modules that make up the code."
                      value={config.dot}
                      onChange={(v) =>
                        setConfig((c) => ({
                          ...c,
                          dot: { ...c.dot, ...v, style: c.dot.style },
                        }))
                      }
                    />
                    <ColorBlock
                      label="Corner squares"
                      description="The 3 finder patterns at the corners."
                      value={config.cornerSquare}
                      onChange={(v) =>
                        setConfig((c) => ({
                          ...c,
                          cornerSquare: {
                            ...c.cornerSquare,
                            ...v,
                            style: c.cornerSquare.style,
                          },
                        }))
                      }
                    />
                    <ColorBlock
                      label="Corner dots"
                      description="The smaller dot inside each corner square."
                      value={config.cornerDot}
                      onChange={(v) =>
                        setConfig((c) => ({
                          ...c,
                          cornerDot: {
                            ...c.cornerDot,
                            ...v,
                            style: c.cornerDot.style,
                          },
                        }))
                      }
                    />
                    <ColorBlock
                      label="Background"
                      description="QR background — set transparent by using same color as page."
                      value={config.background}
                      onChange={(v) =>
                        setConfig((c) => ({
                          ...c,
                          background: { ...c.background, ...v },
                        }))
                      }
                    />
                  </>
                  </FeatureGate>
                )}

                {tab === "shapes" && (
                  <FeatureGate feature="qrShapes" disabledOverlay>
                  <>
                    <Section
                      title="Dot style"
                      description="The shape of each module."
                    >
                      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                        {(
                          [
                            "square",
                            "dots",
                            "rounded",
                            "extra-rounded",
                            "classy",
                            "classy-rounded",
                          ] as DotType[]
                        ).map((s) => (
                          <StyleTile
                            key={s}
                            active={config.dot.style === s}
                            onClick={() =>
                              setConfig((c) => ({
                                ...c,
                                dot: { ...c.dot, style: s },
                              }))
                            }
                            label={s.replace("-", " ")}
                            preview={DotStylePreviews[s]}
                          />
                        ))}
                      </div>
                    </Section>

                    <Section
                      title="Corner square style"
                      description="The 3 large finder patterns."
                    >
                      <div className="grid grid-cols-3 gap-2 max-w-md">
                        {(
                          ["square", "dot", "extra-rounded"] as CornerSquareType[]
                        ).map((s) => (
                          <StyleTile
                            key={s}
                            active={config.cornerSquare.style === s}
                            onClick={() =>
                              setConfig((c) => ({
                                ...c,
                                cornerSquare: {
                                  ...c.cornerSquare,
                                  style: s,
                                },
                              }))
                            }
                            label={s.replace("-", " ")}
                            preview={CornerSquarePreviews[s]}
                          />
                        ))}
                      </div>
                    </Section>

                    <Section
                      title="Corner dot style"
                      description="The inner dot of each corner."
                    >
                      <div className="grid grid-cols-2 gap-2 max-w-xs">
                        {(["square", "dot"] as CornerDotType[]).map((s) => (
                          <StyleTile
                            key={s}
                            active={config.cornerDot.style === s}
                            onClick={() =>
                              setConfig((c) => ({
                                ...c,
                                cornerDot: { ...c.cornerDot, style: s },
                              }))
                            }
                            label={s}
                            preview={CornerDotPreviews[s]}
                          />
                        ))}
                      </div>
                    </Section>
                  </>
                  </FeatureGate>
                )}

                {tab === "logo" && (
                  <FeatureGate feature="qrLogo" disabledOverlay>
                  <>
                    <Section
                      title="Center logo"
                      description="Embed a logo. Best results with transparent PNG or SVG, ≤512px."
                    >
                      {config.logo.src ? (
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-xl border border-ink-200 bg-white flex items-center justify-center overflow-hidden">
                            <img
                              src={config.logo.src}
                              alt=""
                              className="max-w-full max-h-full"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => fileInputRef.current?.click()}
                              icon={<Upload size={12} />}
                            >
                              Replace
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={removeLogo}
                              icon={<X size={12} />}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full h-32 rounded-xl border-2 border-dashed border-ink-200 hover:border-brand-400 hover:bg-brand-50/40 transition-colors flex flex-col items-center justify-center gap-2 text-ink-500 hover:text-brand-700"
                        >
                          <Upload size={20} />
                          <div className="text-sm font-medium">
                            Click to upload logo
                          </div>
                          <div className="text-[11px] text-ink-400">
                            PNG, JPG, SVG up to 1 MB
                          </div>
                        </button>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </Section>

                    {config.logo.src && (
                      <Section title="Logo settings">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <Field
                            label="Size"
                            hint={`${Math.round(config.logo.size * 100)}%`}
                          >
                            <input
                              type="range"
                              min={0.1}
                              max={0.5}
                              step={0.01}
                              value={config.logo.size}
                              onChange={(e) =>
                                setConfig((c) => ({
                                  ...c,
                                  logo: {
                                    ...c.logo,
                                    size: parseFloat(e.target.value),
                                  },
                                }))
                              }
                              className="w-full accent-brand-500"
                            />
                          </Field>
                          <Field
                            label="Margin"
                            hint={`${config.logo.margin}px`}
                          >
                            <input
                              type="range"
                              min={0}
                              max={20}
                              step={1}
                              value={config.logo.margin}
                              onChange={(e) =>
                                setConfig((c) => ({
                                  ...c,
                                  logo: {
                                    ...c.logo,
                                    margin: parseInt(e.target.value),
                                  },
                                }))
                              }
                              className="w-full accent-brand-500"
                            />
                          </Field>
                        </div>
                        <label className="inline-flex items-center gap-2 select-none">
                          <input
                            type="checkbox"
                            checked={config.logo.hideBackgroundDots}
                            onChange={(e) =>
                              setConfig((c) => ({
                                ...c,
                                logo: {
                                  ...c.logo,
                                  hideBackgroundDots: e.target.checked,
                                },
                              }))
                            }
                            className="w-4 h-4 accent-brand-500"
                          />
                          <span className="text-xs text-ink-700">
                            Hide dots behind logo (cleaner look, may require
                            higher error correction)
                          </span>
                        </label>
                      </Section>
                    )}
                  </>
                  </FeatureGate>
                )}

                {tab === "frame" && (
                  <FeatureGate feature="qrAdvanced" disabledOverlay>
                  <>
                    <Section
                      title="Frame"
                      description="Add a branded frame with a call-to-action. PNG/JPEG downloads include it; SVG exports the code only."
                    >
                      <label className="flex items-center gap-2.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={config.frame.enabled}
                          onChange={(e) =>
                            setConfig((c) => ({
                              ...c,
                              frame: { ...c.frame, enabled: e.target.checked },
                            }))
                          }
                          className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-2 focus:ring-brand-500/30"
                        />
                        <span className="text-sm text-ink-700 font-medium flex items-center">
                          Show a frame around the QR
                          <HelpHint title="Frames & call-to-action" anchor="qr-frames">
                            Wrap the code in a frame with a call-to-action like
                            "SCAN ME". The frame is included in PNG/JPEG
                            downloads — handy for print.
                          </HelpHint>
                        </span>
                      </label>
                    </Section>

                    {config.frame.enabled && (
                      <>
                        <Section title="Style">
                          <div className="flex flex-wrap gap-1.5">
                            {(
                              [
                                { v: "rounded", label: "Rounded" },
                                { v: "box", label: "Box" },
                                { v: "banner", label: "Banner" },
                              ] as { v: FrameStyle; label: string }[]
                            ).map(({ v, label }) => (
                              <Chip
                                key={v}
                                active={config.frame.style === v}
                                onClick={() =>
                                  setConfig((c) => ({
                                    ...c,
                                    frame: { ...c.frame, style: v },
                                  }))
                                }
                              >
                                {label}
                              </Chip>
                            ))}
                          </div>
                        </Section>

                        <Section
                          title="Call to action"
                          description="Short text shown under the code."
                        >
                          <input
                            type="text"
                            value={config.frame.text}
                            maxLength={28}
                            onChange={(e) =>
                              setConfig((c) => ({
                                ...c,
                                frame: { ...c.frame, text: e.target.value },
                              }))
                            }
                            placeholder="SCAN ME"
                            className="w-full h-9 px-3 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                          />
                        </Section>

                        <Section title="Colors">
                          <div className="flex items-center gap-5">
                            <Field label="Frame">
                              <input
                                type="color"
                                value={config.frame.color}
                                onChange={(e) =>
                                  setConfig((c) => ({
                                    ...c,
                                    frame: { ...c.frame, color: e.target.value },
                                  }))
                                }
                                className="h-9 w-14 rounded-lg border border-ink-200 cursor-pointer bg-white"
                              />
                            </Field>
                            <Field label="Text">
                              <input
                                type="color"
                                value={config.frame.textColor}
                                onChange={(e) =>
                                  setConfig((c) => ({
                                    ...c,
                                    frame: { ...c.frame, textColor: e.target.value },
                                  }))
                                }
                                className="h-9 w-14 rounded-lg border border-ink-200 cursor-pointer bg-white"
                              />
                            </Field>
                          </div>
                        </Section>
                      </>
                    )}
                  </>
                  </FeatureGate>
                )}

                {tab === "advanced" && (
                  <FeatureGate feature="qrAdvanced" disabledOverlay>
                  <>
                    <Section
                      title="Error correction"
                      description="Higher = more resilient to damage / logos, but denser code."
                    >
                      <div className="flex flex-wrap gap-1.5">
                        {(
                          [
                            { v: "L", label: "Low (~7%)" },
                            { v: "M", label: "Medium (~15%)" },
                            { v: "Q", label: "Quartile (~25%)" },
                            { v: "H", label: "High (~30%)" },
                          ] as { v: ErrorLevel; label: string }[]
                        ).map(({ v, label }) => (
                          <Chip
                            key={v}
                            active={config.errorCorrection === v}
                            onClick={() =>
                              setConfig((c) => ({
                                ...c,
                                errorCorrection: v,
                              }))
                            }
                          >
                            {label}
                          </Chip>
                        ))}
                      </div>
                    </Section>

                    <Section
                      title="Size"
                      description="Output canvas size — affects download resolution."
                    >
                      <div className="flex flex-wrap gap-1.5">
                        {[256, 320, 512, 1024].map((s) => (
                          <Chip
                            key={s}
                            active={config.size === s}
                            onClick={() => setConfig((c) => ({ ...c, size: s }))}
                          >
                            {s}px
                          </Chip>
                        ))}
                      </div>
                    </Section>

                    <Section
                      title="Quiet zone (margin)"
                      description="Recommended ≥ 4 modules around the QR for reliable scanning."
                    >
                      <Field label="Margin" hint={`${config.margin}px`}>
                        <input
                          type="range"
                          min={0}
                          max={40}
                          step={1}
                          value={config.margin}
                          onChange={(e) =>
                            setConfig((c) => ({
                              ...c,
                              margin: parseInt(e.target.value),
                            }))
                          }
                          className="w-full accent-brand-500"
                        />
                      </Field>
                    </Section>
                  </>
                  </FeatureGate>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRStudio;
